/**
 * Per-page access in the app (2026-10-06). The session (/auth/me) brings the user's grants:
 * `['*']` for an admin (SUPERADMIN, TENANTADMIN), else `page:<view id>` and `action:<name>`
 * keys of their role (backend-node lib/page-access.js, edited on the Users & roles page).
 * The app hides what is not granted; the API refuses it either way.
 *
 * Without the field (a backend from before permissions) everything is allowed, as before:
 * the API is what decides.
 */

export type Permissions = readonly string[] | null | undefined

/** Admin roles: the usage and Users & roles pages, and everything else. */
export const ADMIN_ROLES: readonly string[] = ['SUPERADMIN', 'TENANTADMIN']

export function allows(permissions: Permissions, permission: string): boolean {
    if (!permissions) return true
    return permissions.includes('*') || permissions.includes(permission)
}

export function canSeeView(view: { id: string }, permissions: Permissions): boolean {
    return allows(permissions, `page:${view.id}`)
}

/** The views a user may open and the ones their role hides. */
export function splitViews<V extends { id: string }>(views: readonly V[], permissions: Permissions): { allowed: V[]; denied: V[] } {
    const allowed: V[] = []
    const denied: V[] = []
    for (const view of views) (canSeeView(view, permissions) ? allowed : denied).push(view)
    return { allowed, denied }
}

/** An admin: grants `*`, or, from a backend without permissions, an admin role. */
export function isAdmin(session: { role_name?: string | null; permissions?: Permissions }): boolean {
    if (session.permissions) return session.permissions.includes('*')
    return ADMIN_ROLES.includes(String(session.role_name ?? '').toUpperCase())
}
